import React, { useState, useEffect } from 'react';
import { Play, RotateCcw, ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DemoBanner = ({ onOpenGuide }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [resetting, setResetting] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isHidden, setIsHidden] = useState(() => {
    return localStorage.getItem('demo_banner_hidden') === 'true';
  });

  const handleHideBanner = () => {
    setIsHidden(true);
    localStorage.setItem('demo_banner_hidden', 'true');
  };

  const handleResetDemo = async () => {
    if (!window.confirm('Er du sikker på at du vil tilbakestille demo-dataene? Alt blir som nytt.')) {
      return;
    }

    setResetting(true);
    try {
      
      await axios.post(
        `${API}/admin/sandbox/reset`,
        {},
        { }
      );
      
      // Reload page to show fresh data
      window.location.reload();
      
    } catch (err) {
      console.error('Reset demo error:', err);
      alert('Kunne ikke tilbakestille demo. Prøv igjen.');
      setResetting(false);
    }
  };

  const handleExitSandbox = async () => {
    setExiting(true);
    try {
      
      const response = await axios.post(
        `${API}/admin/sandbox/exit`,
        {},
        { }
      );
      
      // Update local storage
      localStorage.removeItem('is_sandbox');
      localStorage.removeItem('sandbox_tenant_id');
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/users/me`, {
        
      });
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      // Redirect to empty dashboard
      window.location.href = '/lexy/dashboard';
      
    } catch (err) {
      console.error('Exit sandbox error:', err);
      alert('Kunne ikke starte ekte tenant. Prøv igjen.');
      setExiting(false);
    }
  };

  // Don't render if hidden
  if (isHidden) {
    return null;
  }

  return (
    <>
      <div className={`border-b ${
        isDarkMode 
          ? 'bg-gray-900/80 border-gray-800' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2">
            {/* Left side - Demo indicator */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Demo
                </span>
              </div>
              <p className={`hidden sm:block text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Test data - utforsk fritt
              </p>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-1">              
              <button
                onClick={handleResetDemo}
                disabled={resetting}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-50 ${
                  isDarkMode 
                    ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-300' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <RotateCcw className={`w-3 h-3 ${resetting ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Reset</span>
              </button>
              
              <button
                onClick={() => setShowExitConfirm(true)}
                disabled={exiting}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Start ekte selskap
                <ArrowRight className="w-3 h-3" />
              </button>

              <button
                onClick={handleHideBanner}
                className={`ml-2 p-1 rounded transition-colors ${
                  isDarkMode 
                    ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-800' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title="Skjul banner"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl max-w-md w-full p-6 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Start ekte selskap?
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Dette vil opprette ditt eget selskap.
                </p>
              </div>
              <button
                onClick={() => setShowExitConfirm(false)}
                className={`transition-colors ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Oppretter et nytt, tomt selskap
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Beholder din profil og innstillinger
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <X className="w-3 h-3 text-red-600" />
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Fjerner all demo-data
                </p>
              </div>
            </div>

            <div className={`rounded-lg p-3 mb-6 ${
              isDarkMode 
                ? 'bg-blue-900/30 border border-blue-800' 
                : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                💡 Du kan alltid åpne demo igjen senere fra menyen
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className={`flex-1 px-4 py-2 rounded-lg border font-medium transition-colors ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Avbryt
              </button>
              <button
                onClick={handleExitSandbox}
                disabled={exiting}
                className="flex-1 px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {exiting ? (
                  <>
                    <RotateCcw className="w-4 h-4 animate-spin" />
                    Oppretter...
                  </>
                ) : (
                  <>
                    Ja, start ekte selskap
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DemoBanner;
